import api from '@/shared/utils/api';
import type {
  AISuggestion,
  PaginatedResponse,
  SupportMetrics,
  Ticket,
  TicketFilters,
  TicketListItem,
} from '../types/support';
import type {
  AssignTicketRequest,
  CreateTicketRequest,
  ReplyTicketRequest,
  SubmitCSATRequest,
  TransitionTicketRequest,
  UploadAttachmentResponse,
} from '../types/api';

const BASE = '/support';

export const supportApi = {
  listTickets: async (filters: TicketFilters): Promise<PaginatedResponse<TicketListItem>> => {
    const { data } = await api.get<PaginatedResponse<TicketListItem>>(`${BASE}/tickets`, {
      params: filters,
    });
    return data;
  },

  listMyTickets: async (): Promise<PaginatedResponse<TicketListItem>> => {
    const { data } = await api.get<PaginatedResponse<TicketListItem>>(`${BASE}/tickets/mine`);
    return data;
  },

  getTicket: async (id: string): Promise<Ticket> => {
    const { data } = await api.get<Ticket>(`${BASE}/tickets/${id}`);
    return data;
  },

  createTicket: async (payload: CreateTicketRequest): Promise<Ticket> => {
    const { data } = await api.post<Ticket>(`${BASE}/tickets`, payload);
    return data;
  },

  reply: async (id: string, payload: ReplyTicketRequest): Promise<Ticket> => {
    const { data } = await api.post<Ticket>(`${BASE}/tickets/${id}/messages`, payload);
    return data;
  },

  transition: async (id: string, payload: TransitionTicketRequest): Promise<Ticket> => {
    const { data } = await api.patch<Ticket>(`${BASE}/tickets/${id}/status`, payload);
    return data;
  },

  assign: async (id: string, payload: AssignTicketRequest): Promise<Ticket> => {
    const { data } = await api.patch<Ticket>(`${BASE}/tickets/${id}/assignee`, payload);
    return data;
  },

  submitCSAT: async (id: string, payload: SubmitCSATRequest): Promise<Ticket> => {
    const { data } = await api.post<Ticket>(`${BASE}/tickets/${id}/csat`, payload);
    return data;
  },

  uploadAttachment: async (
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<UploadAttachmentResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post<UploadAttachmentResponse>(`${BASE}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (e.total && onProgress) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    });
    return data;
  },

  getMetrics: async (range = '30d'): Promise<SupportMetrics> => {
    const { data } = await api.get<SupportMetrics>(`${BASE}/metrics`, {
      params: { range },
    });
    return data;
  },

  getAISuggestion: async (ticketId: string): Promise<AISuggestion> => {
    const { data } = await api.get<AISuggestion>(`${BASE}/tickets/${ticketId}/ai-suggestion`);
    return data;
  },
};
